export interface AdvisorBrand {
  firmName: string
  advisorName?: string
  logoDataUrl?: string
  footerNote: string
}

export interface AdvisorBrandInput {
  firmName: string
  advisorName?: string
  logoDataUrl?: string
  footerNote?: string
}

const KEYS = {
  firmName: 'advisor.brand.firm_name',
  advisorName: 'advisor.brand.advisor_name',
  logoDataUrl: 'advisor.brand.logo_dataurl',
  footerNote: 'advisor.brand.footer_note',
} as const

export const DEFAULT_FOOTER_NOTE = 'Powered by Causeway. Strategy that explains itself.'
const MAX_NAME = 80
const MAX_FOOTER = 200
const MAX_LOGO_BYTES = 200 * 1024

function isAllowedLogo(dataUrl: string): boolean {
  return /^data:image\/(png|jpeg);base64,/.test(dataUrl)
}

function approxBase64Bytes(dataUrl: string): number {
  const idx = dataUrl.indexOf(',')
  if (idx < 0) return dataUrl.length
  return dataUrl.length - idx - 1
}

export function setAdvisorBrand(input: AdvisorBrandInput): void {
  const firmName = (input.firmName || '').slice(0, MAX_NAME)
  if (!firmName) throw new Error('firmName is required')

  if (input.logoDataUrl !== undefined) {
    if (!isAllowedLogo(input.logoDataUrl)) {
      throw new Error('Logo must be a PNG or JPEG data URL')
    }
    if (approxBase64Bytes(input.logoDataUrl) > MAX_LOGO_BYTES) {
      throw new Error('Logo data exceeds 200KB; please use a smaller image')
    }
  }

  localStorage.setItem(KEYS.firmName, firmName)
  if (input.advisorName !== undefined) {
    localStorage.setItem(KEYS.advisorName, input.advisorName.slice(0, MAX_NAME))
  } else {
    localStorage.removeItem(KEYS.advisorName)
  }
  if (input.logoDataUrl !== undefined) {
    localStorage.setItem(KEYS.logoDataUrl, input.logoDataUrl)
  } else {
    localStorage.removeItem(KEYS.logoDataUrl)
  }
  const footer = (input.footerNote ?? DEFAULT_FOOTER_NOTE).slice(0, MAX_FOOTER)
  localStorage.setItem(KEYS.footerNote, footer)
}

export function getAdvisorBrand(): AdvisorBrand | null {
  const firmName = localStorage.getItem(KEYS.firmName)
  if (!firmName) return null
  return {
    firmName,
    advisorName: localStorage.getItem(KEYS.advisorName) || undefined,
    logoDataUrl: localStorage.getItem(KEYS.logoDataUrl) || undefined,
    footerNote: localStorage.getItem(KEYS.footerNote) || DEFAULT_FOOTER_NOTE,
  }
}

export function clearAdvisorBrand(): void {
  Object.values(KEYS).forEach((k) => localStorage.removeItem(k))
}

export function hasUsableBrand(): boolean {
  return Boolean(localStorage.getItem(KEYS.firmName))
}
